import { createContext, useContext, useEffect, useState } from "react"

type Theme = "dark" | "light" | "system"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: "dark",
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "dark",
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  )

  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove("light", "dark")

    const applyTheme = async (themeToApply: "light" | "dark") => {
      root.classList.add(themeToApply)

      // Update Native Status Bar if Capacitor is available
      try {
        const { StatusBar } = await import('@capacitor/status-bar')
        const { Style } = await import('@capacitor/status-bar')

        await StatusBar.setStyle({
          style: themeToApply === 'dark' ? Style.Dark : Style.Light
        })

        // On Android, we make the status bar translucent (overlay)
        // This allows the webview to handle the "safe-area-top" properly
        if (window.navigator.userAgent.includes('Android')) {
          await StatusBar.setOverlaysWebView({ overlay: true })
          // Optional: ensure background is transparent so we see the app color
          await StatusBar.setBackgroundColor({
            color: 'transparent'
          })

          // Sync Navigation Bar color with Bottom Menu
          const syncNavBar = async () => {
            try {
              const { NavigationBar } = await import('@capgo/capacitor-navigation-bar')

              // We set the navigation bar color to match our CSS --card color
              // and explicitly tell Android to use dark buttons in light mode
              await NavigationBar.setNavigationBarColor({
                color: themeToApply === 'dark' ? '#0A0A0A' : '#ffffff',
                darkButtons: themeToApply !== 'dark'
              })
            } catch (e) {
              console.error('Nav bar sync failed', e)
            }
          }

          syncNavBar()
          setTimeout(syncNavBar, 300) // Double check after transition
        }
      } catch (e) {
        // Fallback for browser
      }
    }

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light"

      applyTheme(systemTheme)
      return
    }

    applyTheme(theme)
  }, [theme])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")

  return context
}
